'use es6';

import debounce from 'react-utils/debounce';
import { Alignments } from 'rich-text-lib/constants/alignments';
import { ImageAlignmentClasses } from 'rich-text-lib/constants/image';
import { hsimageConfig } from '../constants/image';
import { INSERT_IMAGE, REPLACE_IMAGE, EDIT_IMAGE } from '../constants/usageTracking';
import { getAtomValue } from '../data/atomUtils';
import { autocompleteActiveRangeAtom } from '../data/autocompleteAtoms';
import { swapCommandForContent } from '../hsautocomplete/utils';
import { trackInteraction } from '../utils/usageTracking';
import { isNestedOnlyChild } from '../hsformatting/utils';
import { isEditableImage, getImagePluginSetting, getImageAlignment, getMarginValue, retainAttributesOfReplacedImage, updateImageAlignmentClass, getAltTextForFile, getImageLoadingDefaultValue, getImageGenerationPrompt, getFileSource } from '../utils/image';
import { resizeAssetDimensions, getMaxDimensions, CommandToAlignmentMap, getAlignment } from '../utils/shared';
import { getUpdatesForLayout } from './utils/layouts';
import { getFetchFileByUrl } from 'tinymce-data/api/FileManager';
import { getPluginSetting } from '../utils';
import { STYLE_OPTIONS } from '../constants/image';
function trackImageInsert(editor, imageReplaced, {
  fileSource
} = {}) {
  trackInteraction(editor, 'tinymceImage', imageReplaced ? REPLACE_IMAGE : INSERT_IMAGE, {
    fileSource,
    version: 'contextualToolbar',
    replacedImgSrc: imageReplaced ? imageReplaced.src : undefined
  });
}
function _trackImageEdit(editor, editType, imgSrc) {
  trackInteraction(editor, 'tinymceEditImage', EDIT_IMAGE, {
    editType,
    imgSrc,
    version: 'contextualToolbar'
  });
}
const trackImageEdit = debounce(_trackImageEdit, 500);
export function getImageAttributes(editor, image) {
  if (!image) return {};
  const dom = editor.dom;
  const win = editor.getWin();
  const computedStyle = win.getComputedStyle(image);
  const altText = dom.getAttrib(image, 'alt') || '';
  const loadingValue = dom.getAttrib(image, 'loading', 'default');
  const width = parseInt(dom.getAttrib(image, 'width'), 10) || image.width;
  const heightAttr = parseInt(dom.getAttrib(image, 'height'), 10);
  const height = heightAttr || Math.round(image.height / image.width * width);
  const shouldDisableResizingPredicate = getImagePluginSetting(editor, hsimageConfig.DISABLE_RESIZING_PREDICATE);
  const showDimensionsControls = !shouldDisableResizingPredicate || !shouldDisableResizingPredicate(editor, image);
  const showMarginControls = !getImagePluginSetting(editor, hsimageConfig.DISABLE_SPACING);
  const maxContainerWidth = getImagePluginSetting(editor, hsimageConfig.MAX_DIMENSION_WIDTH);
  return {
    alignment: getImageAlignment(editor, image),
    altText,
    loadingValue,
    naturalWidth: image.naturalWidth,
    naturalHeight: image.naturalHeight,
    showDimensionsControls,
    showMarginControls,
    dimensions: {
      width,
      height
    },
    maxDimensions: getMaxDimensions(maxContainerWidth, width, height),
    margin: {
      top: getMarginValue(image, computedStyle, 'margin-top'),
      left: getMarginValue(image, computedStyle, 'margin-left'),
      right: getMarginValue(image, computedStyle, 'margin-right'),
      bottom: getMarginValue(image, computedStyle, 'margin-bottom')
    }
  };
}
export function getFetchImageFileInfo(editor) {
  const apiClient = editor.getParam('apiClient');
  const fetchFileByUrl = getFetchFileByUrl(apiClient);
  return image => {
    return new Promise(resolve => {
      const src = image.src;
      fetchFileByUrl(src).then(fileInfo => {
        const {
          name,
          size,
          extension,
          url
        } = fileInfo;
        resolve({
          name: `${name}.${extension}`,
          size,
          src: url
        });
      }).catch(error => {
        console.error(error);
        resolve({
          name: src,
          src
        });
      });
    });
  };
}
export function getRemoveImageHandler(editor) {
  return image => {
    const dom = editor.dom;
    const parent = image.parentNode;
    const isOnlyChild = isNestedOnlyChild(image, image.parentNode);
    editor.undoManager.transact(() => {
      editor.selection.collapse();
      if (parent && isOnlyChild && ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'PRE', 'A'].includes(image.parentNode.nodeName)) {
        dom.remove(parent);
      } else {
        const bookmark = editor.selection.getBookmark();
        dom.remove(image);
        editor.selection.moveToBookmark(bookmark);
      }
      editor.nodeChanged();
    });
    trackImageEdit(editor, 'removeImage', image.src);
  };
}
export function getInsertImageHandler(editor, imageToReplace, initialPanel) {
  const autoMaxWidth = !!getImagePluginSetting(editor, hsimageConfig.AUTO_MAX_WIDTH);
  const useAlignmentClasses = !!getImagePluginSetting(editor, hsimageConfig.USE_ALIGNMENT_CLASSES);
  const imageLoadingDefaultValue = getImageLoadingDefaultValue(editor);
  const useCustomAltText = getImagePluginSetting(editor, 'useCustomAltText');
  return file => {
    let image;
    editor.undoManager.transact(() => {
      const dom = editor.dom;
      const {
        width: naturalWidth,
        height: naturalHeight,
        url
      } = file;
      const {
        width,
        height,
        styles,
        className,
        loading
      } = retainAttributesOfReplacedImage(editor, imageToReplace, naturalWidth, naturalHeight);
      const attributes = {
        id: '__hsnew',
        src: url,
        width,
        height,
        loading,
        alt: getAltTextForFile(file, useCustomAltText),
        'data-gai': getImageGenerationPrompt(file)
      };
      if (height) {
        styles.height = 'auto';
        if (!useAlignmentClasses) {
          styles['max-width'] = '100%';
        }
      }
      if (width) {
        styles.width = `${width}px`;
        if (autoMaxWidth) {
          styles['max-width'] = `${naturalWidth}px`;
        }
      }
      const serializedStyles = dom.serializeStyle(styles);
      if (serializedStyles) {
        attributes.style = serializedStyles;
      }
      if (useAlignmentClasses) {
        attributes.class = className || ImageAlignmentClasses[Alignments.NONE];
      }
      if (imageToReplace) {
        attributes.loading = loading || imageLoadingDefaultValue;
      } else {
        attributes.loading = imageLoadingDefaultValue;
      }
      editor.focus();
      const newImage = dom.createHTML('img', attributes);
      if (initialPanel === 'IMAGE_GENERATION' && getAtomValue(autocompleteActiveRangeAtom)) {
        swapCommandForContent(editor, newImage);
      } else {
        editor.selection.setContent(newImage);
      }
      image = dom.get('__hsnew');
      editor.execCommand('hsInsertNewline', false, {
        target: image
      });
      dom.setAttrib(image, 'id', null);
      editor.selection.select(image);
    });
    trackImageInsert(editor, imageToReplace, {
      fileSource: getFileSource(file)
    });
  };
}
export function getLayoutChangeHandler(editor) {
  const useAlignmentClasses = !!getImagePluginSetting(editor, hsimageConfig.USE_ALIGNMENT_CLASSES);
  const showMarginControls = !getImagePluginSetting(editor, hsimageConfig.DISABLE_SPACING);
  return (image, layoutType, currentAlignment) => {
    if (layoutType === currentAlignment) return;
    const {
      command,
      width,
      height,
      margin
    } = getUpdatesForLayout(layoutType, image);
    editor.undoManager.transact(() => {
      editor.fire('ObjectResized', {
        target: image,
        width,
        height
      });
      if (useAlignmentClasses) {
        updateImageAlignmentClass(editor, image, ImageAlignmentClasses[layoutType]);
      } else {
        editor.execCommand(command);
      }
      if (showMarginControls) {
        editor.dom.setStyle(image, 'margin', margin);
      }
    });
    trackImageEdit(editor, 'alignment');
  };
}
export function getDimensionsChangeHandler(editor) {
  return (image, dimensions) => {
    const {
      height,
      width
    } = dimensions;
    editor.fire('ObjectResized', {
      target: image,
      constrained: true,
      width,
      height
    });
    trackImageEdit(editor, 'dimensions');
  };
}
export function getUpdateMarginHandler(editor) {
  return (image, margin) => {
    const {
      top,
      left,
      right,
      bottom
    } = margin;
    editor.undoManager.transact(() => {
      editor.dom.setStyle(image, {
        'margin-top': top,
        'margin-left': left,
        'margin-right': right,
        'margin-bottom': bottom
      });
    });
    trackImageEdit(editor, 'margin');
  };
}
export function getAltTextChangeHandler(editor) {
  return (image, altText) => {
    editor.undoManager.transact(() => {
      image.setAttribute('alt', altText);
    });
    trackImageEdit(editor, 'altText');
  };
}
export function getLoadingValueChangeHandler(editor) {
  return (image, loadingValue) => {
    editor.undoManager.transact(() => {
      editor.dom.setAttrib(image, 'loading', loadingValue);
    });
    trackImageEdit(editor, 'loadingValue');
  };
}
export function getResizeImageHandler(editor) {
  const autoMaxWidth = !!getImagePluginSetting(editor, hsimageConfig.AUTO_MAX_WIDTH);
  const maxContainerWidth = getImagePluginSetting(editor, hsimageConfig.MAX_DIMENSION_WIDTH);
  const useAlignmentClasses = !!getImagePluginSetting(editor, hsimageConfig.USE_ALIGNMENT_CLASSES);
  return evt => {
    const {
      target,
      constrained
    } = evt;
    if (!isEditableImage(editor, evt.target)) return;
    const {
      dom
    } = editor;
    let {
      width,
      height
    } = resizeAssetDimensions(maxContainerWidth, evt.width, evt.height);
    editor.undoManager.transact(() => {
      if (width) {
        width = Math.round(width);
        height = Math.round(height);
        dom.setAttrib(target, 'width', width);
        dom.setStyle(target, 'width', `${width}px`);
        dom.setAttrib(target, 'height', height);
        dom.setStyle(target, 'height', 'auto');
        if (autoMaxWidth) {
          dom.setStyle(target, 'max-width', `${width}px`);
        } else if (!useAlignmentClasses) {
          dom.setStyle(target, 'max-width', '100%');
        }
      }
      if (constrained === false) {
        height = Math.round(height);
        dom.setStyle(target, 'height', `${height}px`);
        dom.setAttrib(target, 'height', height);
      }
    });
    trackImageEdit(editor, 'dimensions');
  };
}
const updateAlignmentForEmail = (editor, target, alignment) => {
  const $target = editor.$(target);
  const alignImage = ($image, shouldSelectImage) => {
    const wrappedInCenter = $image.parent().is('center');

    // Centered images need to be inside a <center>
    if (alignment === 'center' && !wrappedInCenter) {
      $image.wrap('<center></center>');
    } else if (wrappedInCenter) {
      $image.parent().replaceWith($image);
    }
    const curAlignment = $image.attr('align');
    if (curAlignment !== alignment && ['left', 'center', 'right'].includes(alignment)) {
      $image.attr('align', alignment);
    } else {
      $image.attr('align', null);
    }
    if (shouldSelectImage) {
      editor.selection.select($image[0]);
    }
  };

  // Handle both image selections and selections that might
  // contain images
  if (isEditableImage(editor, target)) {
    alignImage($target, true);
  } else {
    const images = $target.find('img');
    images.each((image, idx) => {
      alignImage(editor.$(image), idx === images.length - 1);
    });
  }
};
export function getEmailAlignCommandHandler(editor) {
  return evt => {
    const cmd = evt.command.toLowerCase();
    if (cmd.indexOf('justify') !== 0 || editor.selection.isCollapsed()) {
      return;
    }

    // Email image alignment tweaks for Outlook
    const alignment = cmd.replace('justify', '');
    const target = editor.selection.getNode();
    updateAlignmentForEmail(editor, target, alignment);
  };
}
export function getLayoutChangeHandlerV2(editor) {
  const useAlignmentClasses = !!getImagePluginSetting(editor, hsimageConfig.USE_ALIGNMENT_CLASSES);
  return (target, alignment) => {
    editor.undoManager.transact(() => {
      if (useAlignmentClasses) {
        let className = ImageAlignmentClasses[alignment];
        if (target.classList.contains(className)) {
          alignment = Alignments.NONE;
          className = ImageAlignmentClasses[alignment];
        }
        updateImageAlignmentClass(editor, target, className);
      } else {
        const curAlignment = getAlignment(target);
        if (alignment === curAlignment) {
          alignment = Alignments.NONE;
        }
        const alignStyles = STYLE_OPTIONS[alignment];
        if (alignStyles) {
          editor.dom.setStyles(target, alignStyles);
        }
      }
      if (getPluginSetting(editor, 'isEmail')) {
        updateAlignmentForEmail(editor, target, alignment);
      }
    });
    trackImageEdit(editor, 'alignment');
  };
}
export function getBeforeExecCommandHandler(editor) {
  const useAlignmentClasses = !!getImagePluginSetting(editor, hsimageConfig.USE_ALIGNMENT_CLASSES);
  return evt => {
    const cmd = evt.command.toLowerCase();
    if (cmd.indexOf('justify') !== 0) return;
    const target = editor.selection.getNode();
    if (!isEditableImage(editor, target) || !useAlignmentClasses) return;

    // Intercept the mce command and update the alignment classnames
    let layoutType = CommandToAlignmentMap[cmd];
    let className = ImageAlignmentClasses[layoutType];
    if (target.classList.contains(className)) {
      layoutType = Alignments.NONE;
      className = ImageAlignmentClasses[layoutType];
    }
    const {
      width,
      height
    } = getUpdatesForLayout(layoutType, target);
    editor.undoManager.transact(() => {
      editor.fire('ObjectResized', {
        target,
        width,
        height
      });
      updateImageAlignmentClass(editor, target, className);
    });
    evt.preventDefault();
  };
}